import { useEffect, useState } from "react";
import { isToday, isWithinInterval, startOfDay, endOfDay } from "date-fns";
import dayjs from "dayjs";
import { Tag, message, Modal } from "antd";
import TaskForm from "../../../task/TaskForm";

import { cn } from "../../../../lib/utils";

import WeekEquipmentTaskTile from "./WeekEquipmentTaskTile";
import { createTask } from "../../../../api/task";

const daysOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export default function WeeklyCalendar({
  startDate,
  equipments,
  tasks,
  onTaskUpdate,
}) {
  const [tasksList, setTasksList] = useState(() =>
    tasks?.filter((t) => t.isActive)
  );
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskFormInstance, setTaskFormInstance] = useState();
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [taskFormValues, setTaskFormValues] = useState(null);

  const populateTaskForm = () => {
    taskFormInstance?.setFieldsValue({
      equipment: { value: taskFormValues.equipment.value, label: taskFormValues.equipment.label },
      startDate: taskFormValues.startDate,
      dueDate: taskFormValues.dueDate,
    });
  }

  useEffect(() => {
    setTasksList(tasks?.filter((t) => t.isActive));
  }, [tasks]);

  useEffect(() => {
    if (selectedEquipment) {
      setShowTaskForm(true);
    } else {
      setShowTaskForm(false);
    }
  }, [selectedEquipment]);

  return (
    <div className="relative">
      <div className="flex gap-2 mb-2 justify-end">
        <Tag className="bg-sky-400 text-sky-700 border-sky-900">
          Task Allocated
        </Tag>
        <Tag className="bg-orange-400 text-orange-900 border-orange-900">
          Worklog Created
        </Tag>
        <Tag className="bg-green-400 text-green-900 border-green-900">
          Downloaded
        </Tag>
      </div>
      <div className="grid grid-cols-8 sticky top-0 left-0">
        <div className="h-full w-full bg-white"></div>
        {daysOfWeek.map((day, index) => {
          const currentDate = dayjs(startDate)
            .clone()
            .add(index, "days")
            .toDate();
          return (
            <div
              key={index}
              className={cn(
                "p-4 border border-solid border-t-0 border-r-0 border-gray-300 text-start",
                isToday(currentDate) ? "bg-blue-100 " : "bg-white",
                index === 0 && "border-l-0",
                index === 6 && "border-r-0",
                index === 0 && !isToday(currentDate) && " bg-gray-100",
                index === 6 && !isToday(currentDate) && " bg-gray-100"
              )}
            >
              <div className="text-gray-500 text-xs">{day}</div>
              <div className="text-xl font-normal">{currentDate.getDate()}</div>
            </div>
          );
        })}
      </div>
      {equipments &&
        equipments?.length &&
        equipments
          ?.sort((a, b) => a.name.localeCompare(b.name))
          .map((e, index) => {
            return (
              <div className="grid grid-cols-8" key={index}>
                <div>
                  <p className="text-gray-500 ">{e.name}</p>
                </div>

                {daysOfWeek.map((day, i) => {
                  const currentDate = dayjs(startDate)
                    .clone()
                    .add(i, "days")
                    .toDate();

                  const allTasks = tasksList?.filter((t) => {
                    const isTaskInThisDate = isWithinInterval(currentDate, {
                      start: startOfDay(t.startDate),
                      end: endOfDay(t.dueDate),
                    });

                    if (!isTaskInThisDate) return false;

                    return t.equipment?.id === e.id && t.equipment?.isActive;
                  });

                  const task = allTasks?.[0];

                  return (
                    <div
                      key={`${i} - ${task?.id}`}
                      className={cn(
                        "cursor-pointer border border-solid border-t-0 border-r-0 border-gray-300 text-start p-1.5",
                        isToday(currentDate) ? "bg-blue-100 " : "bg-white",
                        i === 0 && "border-l-0",
                        i === 6 && "border-r-0",
                        i === 0 && !isToday(currentDate) && " bg-gray-100",
                        i === 6 && !isToday(currentDate) && " bg-gray-100"
                      )}
                      onClick={() => {
                        if (!task) {

                          setSelectedEquipment(e);
                          setTaskFormValues({
                            equipment: { value: e.id, label: e.name },
                            startDate: dayjs(currentDate),
                            dueDate: dayjs(currentDate).add(1, "hour"),
                          })

                        }
                      }}
                    >
                      {!!allTasks.length ? (
                        allTasks.map((task, i) => {
                          return (
                            <WeekEquipmentTaskTile
                              className={`h-[68px]`}
                              task={task}
                              date={currentDate}
                              onUpdate={async () => {
                                await onTaskUpdate();
                              }}
                            />
                          );
                        })
                      ) : (
                        <div className={"h-[68px]"}></div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
      <Modal
        title="Create Task"
        open={showTaskForm}
        onCancel={() => {
          setSelectedEquipment(null);
          setShowTaskForm(false);
        }}
        onOk={async () => {
          const { equipment, ...values } =
            await taskFormInstance?.validateFields();

          if (values) {
            try {
              const res = await createTask({
                ...values,
                equipment: equipment.value,
              });
              if (res) {
                message.success("Task created successfully");
                setSelectedEquipment(null);
                setShowTaskForm(false);
                await onTaskUpdate();
              }
            } catch (error) {
              console.log(error);
              message.error("Something went wrong");
            }
          }
        }}
      >
        <TaskForm
          onFormInstanceReady={(instance) => {
            setTaskFormInstance(instance);
            populateTaskForm()
          }}
        />
      </Modal>
    </div>
  );
}
